import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import routes from "constants/routes";
import { SESSION_KEY } from "constants/key";
import {
  getTiresizeAspectRatioListApi,
  getTiresizeRimDiamListApi,
  getTiresizeWidthListApi,
} from "../api/tiresizeApi";
import { TireSize, TiresizeAspectRatio, TiresizeRimDiam, TiresizeWidth } from "../types/tire";

const tireSizeDefault: TireSize = { tire_width: 0, tire_aspect_ratio: 0, tire_rim_diam: "" };

/**
 * Hook for page changelog
 */
export default function useTiresize() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [widthList, setWidthList] = useState<TiresizeWidth[]>([]);
  const [aspectRatioFrontList, setAspectRatioFrontList] = useState<TiresizeAspectRatio[]>([]);
  const [aspectRatioRearList, setAspectRatioRearList] = useState<TiresizeAspectRatio[]>([]);
  const [rimDiamFrontList, setRimDiamFrontList] = useState<TiresizeRimDiam[]>([]);
  const [rimDiamRearList, setRimDiamRearList] = useState<TiresizeRimDiam[]>([]);
  const [frontTire, setFrontTire] = useState<TireSize>(tireSizeDefault);
  const [rearTire, setRearTire] = useState<TireSize>(tireSizeDefault);

  const getTiresizeWidthList = async () => {
    try {
      const data = await getTiresizeWidthListApi();
      setWidthList(data.list);
    } finally {
      setLoading(false);
    }
  };

  const getTiresizeAspectRatioList = async (body: { tire_width: number }, type: string) => {
    try {
      const data = await getTiresizeAspectRatioListApi(body);
      if (type === "front") {
        setAspectRatioFrontList(data.list);
        setRimDiamFrontList([]);
        return;
      }
      setAspectRatioRearList(data.list);
      setRimDiamRearList([]);
    } finally {
      setLoading(false);
    }
  };

  const getRimDiamList = async (
    body: { tire_width: number; tire_aspect_ratio: number },
    type: string,
  ) => {
    try {
      const data = await getTiresizeRimDiamListApi(body);
      if (type === "front") {
        setRimDiamFrontList(data.list);
        return;
      }
      setRimDiamRearList(data.list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTiresizeWidthList();
  }, []);

  useEffect(() => {
    if (frontTire.tire_width) {
      getTiresizeAspectRatioList({ tire_width: frontTire.tire_width }, "front");
    }
  }, [frontTire.tire_width]);

  useEffect(() => {
    if (rearTire.tire_width) {
      getTiresizeAspectRatioList({ tire_width: rearTire.tire_width }, "rear");
    }
  }, [rearTire.tire_width]);

  useEffect(() => {
    if (frontTire.tire_width && frontTire.tire_aspect_ratio) {
      getRimDiamList(
        { tire_width: frontTire.tire_width, tire_aspect_ratio: frontTire.tire_aspect_ratio },
        "front",
      );
    }
  }, [frontTire.tire_width, frontTire.tire_aspect_ratio]);

  useEffect(() => {
    if (rearTire.tire_width && rearTire.tire_aspect_ratio) {
      getRimDiamList(
        { tire_width: rearTire.tire_width, tire_aspect_ratio: rearTire.tire_aspect_ratio },
        "rear",
      );
    }
  }, [rearTire.tire_width, rearTire.tire_aspect_ratio]);

  const handleSubmit = () => {
    sessionStorage.removeItem(SESSION_KEY.SELECTED_MAKER);
    sessionStorage.removeItem(SESSION_KEY.SELECTED_CATEGORY);
    navigate(routes.mstProcFee);
  };

  return {
    widthList,
    aspectRatioFrontList,
    rimDiamFrontList,
    frontTire,
    setFrontTire,
    aspectRatioRearList,
    rimDiamRearList,
    rearTire,
    setRearTire,
    loading,
    handleSubmit,
  };
}
